import {useMemo} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {Lapp02Page} from '@/pages/components/audit-program/l-section/constants';
import {LAPP02_PAGE} from '@/pages/components/audit-program/l-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface Lapp02SheetViewProps {
    header: AuditProgramSectionVO;
    page: Lapp02Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.65rem', py: 0.6, px: 0.35, lineHeight: 1.2};
const bodySx = {...cellBorder, py: 0.35, px: 0.3, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** Lapp02 — Trade payables circularization detail sheet. */
export default function Lapp02SheetView({
    header,
    page,
    refCode = 'Lapp02',
    values,
    onChange,
}: Lapp02SheetViewProps) {
    const meta = {
        title: page.title || LAPP02_PAGE.title,
        blankRows: page.blankRows || LAPP02_PAGE.blankRows,
        columns: page.columns?.length ? page.columns : LAPP02_PAGE.columns,
    };
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, meta.blankRows)}, (_, i) => `r${i + 1}`),
        [meta.blankRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 1100, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <TableHead>
                        <TableRow>
                            {meta.columns.map((col) => (
                                <TableCell key={col.id} sx={headSx} align="center">
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((rid) => (
                            <TableRow key={rid}>
                                {meta.columns.map((col) => (
                                    <TableCell key={col.id} sx={bodySx}>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            align={col.id === 'supplier' || col.id === 'altProcedures' ? 'left' : 'right'}
                                            sx={fieldSx}
                                            value={values[`${rid}.${col.id}`] ?? ''}
                                            onChange={(e) => onChange(`${rid}.${col.id}`, e.target.value)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
